import { DateTimeHelper, MaskHelper } from "helpers";
import { Application } from "models/Application";
import { DataPairList, KeyPairList } from "pages/NewApplication/steps/ApplicationReview/layout";
import { ApplicationDocumentValidationStatus, ApplicationFormData, ApplicationVehicleTreatedNames } from "pages/NewApplication/steps/types";
import React, { useMemo } from "react";
import { Tooltip } from "react-tooltip";

export default function ApplicationSummary({
	values,
	showBackofficeFields = false
}: {
	values: Application | ApplicationFormData;
	showBackofficeFields?: boolean;
}): JSX.Element {
	const { applicantData, applicationVehicle } = values;

	const reviewData = useMemo<{ title?: string; data: DataPairList }[]>(() => {
		const submittedData =
			values instanceof Application
				? [
						{ label: "Processo", value: values.applicationProcessCode },
						{ label: "Data do envio", value: values.issuedAt ? DateTimeHelper.formatDate(new Date(values.issuedAt), "dd/MM/yyyy") : "-" }
				  ]
				: [];
		const backofficeFields =
			values instanceof Application && showBackofficeFields
				? [
						{
							label: "Prazo para análise",
							value: values.analysisLimitDatetime
								? DateTimeHelper.formatDate(new Date(`${values.analysisLimitDatetime}T00:00:00`), "dd/MM/yyyy")
								: "-"
						}
				  ]
				: [];
		const concessionaireName = values instanceof Application ? values.concessionaireName : values.concessionaire?.name;
		const exemptionTypeName = values instanceof Application ? values.exemptionTypeName : values.exemptionType?.name;

		const fillingMethodTooltipText = () => {
			const baseText = "Os campos a seguir foram preenchidos manualmente: <br/><br/>";
			const listOfFields = applicationVehicle.fieldsMissingFromApi;
			const displayNames = listOfFields
				.map((field) => `- ${ApplicationVehicleTreatedNames[field as keyof typeof ApplicationVehicleTreatedNames]}` || field)
				.join("<br/>");

			return `${baseText} ${displayNames}`;
		};

		const fillingMethod = () => {
			switch (applicationVehicle.documentValidationStatus) {
				case ApplicationDocumentValidationStatus.VALIDATION_SUCCESS:
					if (
						!applicationVehicle.fieldsMissingFromApi ||
						applicationVehicle.fieldsMissingFromApi.length === 0 ||
						applicationVehicle.fieldsMissingFromApi.every((item) => !item || item.trim() === "")
					)
						return "Automático";

					return (
						<div>
							<span
								data-tooltip-id="partially-filled-by-api-tooltip"
								data-tooltip-html={fillingMethodTooltipText()}
								data-tooltip-place="top"
							>
								Automático*
							</span>
							<Tooltip id="partially-filled-by-api-tooltip" />
						</div>
					);

				case ApplicationDocumentValidationStatus.NOT_VALIDATED:
				case ApplicationDocumentValidationStatus.API_UNAVAILABLE:
					return "Manual";
				default:
					return "-";
			}
		};

		return [
			{
				title: "",
				data: [
					...submittedData,
					...backofficeFields,
					{ label: "Concessionária", value: concessionaireName },
					{ label: "Tipo de isenção", value: exemptionTypeName }
				]
			},
			{
				title: "Dados do solicitante",
				data: [
					{ label: "Nome/Razão Social", value: applicantData.applicantName },
					{ label: "CPF/CNPJ", value: MaskHelper.mixedCpfCnpj(applicantData.applicantDocument) },
					{ label: "E-mail", value: applicantData.applicantEmail },
					{ label: "Celular", value: MaskHelper.phone(applicantData.applicantPhoneNumber) },
					{
						label: "Endereço",
						value: `${applicantData.applicantAddressStreet}, ${applicantData.applicantAddressNumber}${
							applicantData.applicantAddressComplement ? ` - ${applicantData.applicantAddressComplement}` : ""
						}`
					},
					{ label: "Bairro", value: applicantData.applicantAddressNeighbourhood },
					{ label: "CEP", value: applicantData.applicantAddressPostalCode },
					{ label: "Cidade / UF", value: `${applicantData.applicantAddressCity}, ${applicantData.applicantAddressState}` }
				]
			},
			{
				title: "Dados do veículo",
				data: [
					{ label: "Preenchimento", value: fillingMethod() },
					{ label: "Placa do veículo", value: applicationVehicle.plate },
					{ label: "Renavam", value: applicationVehicle.renavamCode },
					{
						label: "Modelo",
						value: `${applicationVehicle.vehicleBrand ? `${applicationVehicle.vehicleBrand}/` : ""}${applicationVehicle.vehicleModel}`
					},
					{ label: "Cor", value: applicationVehicle.color },
					{ label: "Ano fabricação", value: applicationVehicle.manufactureYear },
					{ label: "Ano modelo", value: applicationVehicle.modelYear },
					{ label: "Número da TAG", value: applicationVehicle.tagNumber || "-" },
					{ label: "Empresa responsável", value: applicationVehicle.tagCompany?.name || "-" }
				]
			}
		];
	}, [values]);

	return (
		<div className="application-summary border border-neutral-high-200 rounded-lg bg-neutral-high-pure-50">
			<h2 className="text-base font-semibold border-b border-neutral-high-200 px-8 pt-6 pb-4">Detalhes da solicitação</h2>
			<div className="px-8 pb-2 pt-4">
				{reviewData.map(({ title, data }, index) => (
					<React.Fragment key={`review-data-${index}`}>
						{title && <h3 className="font-semibold text-sm py-6">{title}</h3>}
						<KeyPairList data={data} />
					</React.Fragment>
				))}
			</div>
		</div>
	);
}
